import { Scene } from '@babylonjs/core/scene';
import { ViewpointNode } from '@kb3d/models/classes/viewpoints/viewpoint-node';
import { dataURLtoBlob, downloadBlob } from '@kb3d/view/helpers/blob-helper';
import { KbViewer } from '../kb-viewer';

export enum eSnapshotType {
    image = 'image',
    blob = 'blob',
    dataUrl = 'dataUrl',
}

export interface ISnapshotArgs {
    dataType: eSnapshotType;
    width: number;
    height: number;
    frameCamera?: boolean;
    framePadding?: number;
    viewpoint?: string;
    mimeType?: string; //defaults to "image/png"
    filename?: string;
}

export class SnapshotModule {
    constructor(private v: KbViewer, private scene: Scene) {}

    public take(args: ISnapshotArgs): Promise<string | Blob | void> {
        if (args.dataType == eSnapshotType.image) {
            return this.download(args);
        } else if (args.dataType == eSnapshotType.blob) {
            return this.takeAsBlob(args);
        } else {
            return this.takeAsDataUrl(args);
        }
    }

    public takeAsDataUrl(args: ISnapshotArgs): Promise<string> {
        //set the viewpoint
        let viewpointSet = false;
        let originalRenderWidth = 0;
        let originalRenderHeight = 0;
        if (args.viewpoint) {
            const viewpoint = this.v.sceneNode._manager!.getNodeByIdOrName<ViewpointNode>(args.viewpoint);
            if (!viewpoint) {
                throw `Could not find viewpoint ${args.viewpoint} for snapshot`;
            } else {
                this.v.camera.storeCameraSettings();
                this.v.camera.moveCameraToViewpoint(viewpoint);
                viewpointSet = true;
            }
        }

        return this.v
            .whenAssetsLoaded()
            .then(() => {
                const engine = this.v.scene.getEngine();
                if (engine) {
                    const renderingCanvas = engine.getRenderingCanvas();
                    if (renderingCanvas) {
                        originalRenderWidth = renderingCanvas.width;
                        originalRenderHeight = renderingCanvas.height;
                        renderingCanvas.width = args.width;
                        renderingCanvas.height = args.height;
                        renderingCanvas.style.width = `${args.width}px`;
                        renderingCanvas.style.height = `${args.height}px`;
                    }
                    engine.resize();
                    return this.v.forceAndWaitForNextRender();
                }
            })
            .then(() => {
                if (args.frameCamera) {
                    this.v.camera.frameCamera(this.v.sceneNode, {
                        padding: args.framePadding,
                    });
                }
                return this.v.forceAndWaitForNextRender().then(() => {
                return this.v.forceAndWaitForNextRender();
                });
            })
            .then(() => {
                const engine = this.v.scene.getEngine();
                if (!engine) {
                    return '';
                }
                const renderingCanvas = engine.getRenderingCanvas();
                if (!renderingCanvas) {
                    return '';
                }

                const maxTries = 10;

                const mimeType = args.mimeType || 'image/png';
                const snapshotCanvas = document.createElement('canvas');
                snapshotCanvas.width = args.width;
                snapshotCanvas.height = args.height;
                const snapshotTargetContext = snapshotCanvas.getContext('2d');
                const ratio = engine.getRenderWidth() / engine.getRenderHeight();
                let newWidth = args.width;
                let newHeight = newWidth / ratio;
                if (newHeight > args.height) {
                    newHeight = args.height;
                    newWidth = newHeight * ratio;
                }
                const offsetX = Math.max(0, args.width - newWidth) / 2;
                const offsetY = Math.max(0, args.height - newHeight) / 2;

                if (!snapshotTargetContext) {
                    return '';
                }

                return this.attemptScreenshot(renderingCanvas, snapshotTargetContext, {
                    offsetX,
                    offsetY,
                    newWidth,
                    newHeight,
                    maxTries,
                    snapshotWidth: args.width,
                    snapshotHeight: args.height,
                }).then(() => {
                    renderingCanvas.width = originalRenderWidth;
                    renderingCanvas.height = originalRenderHeight;
                    renderingCanvas.style.width = `${originalRenderWidth}px`;
                    renderingCanvas.style.height = `${originalRenderHeight}px`;
                    engine.resize();

                    const dataUrl = snapshotCanvas.toDataURL(mimeType);
                    if (viewpointSet) {
                        this.v.camera.restoreCameraSettings();
                    }
                    snapshotCanvas.width = 0;
                    snapshotCanvas.height = 0;
                    return dataUrl;
                });
            });
    }

    private attemptScreenshot(
        renderingCanvas: HTMLCanvasElement,
        snapshotTargetContext: CanvasRenderingContext2D,
        options: {
            offsetX: number;
            offsetY: number;
            newWidth: number;
            newHeight: number;
            maxTries: number;
            snapshotWidth: number;
            snapshotHeight: number;
        },
        tries = 0
    ) {
        return new Promise<void>(resolve => {
            if (tries < options.maxTries) {
                this.v.forceAndWaitForNextRender().then(() => {
                    snapshotTargetContext.drawImage(
                        renderingCanvas,
                        options.offsetX,
                        options.offsetY,
                        options.newWidth,
                        options.newHeight
                    );

                    // Check if the screenshot is blank by verifying if all pixels are the same color
                    const imageData = snapshotTargetContext.getImageData(
                        0,
                        0,
                        options.snapshotWidth,
                        options.snapshotHeight
                    );
                    const data = imageData.data;

                    // Get the first pixel's RGBA values
                    const firstR = data[0];
                    const firstG = data[1];
                    const firstB = data[2];
                    const firstA = data[3];

                    // Check if any pixel is different from the first pixel
                    let hasVariation = false;
                    for (let i = 4; i < data.length; i += 4) {
                        if (
                            data[i] !== firstR ||
                            data[i + 1] !== firstG ||
                            data[i + 2] !== firstB ||
                            data[i + 3] !== firstA
                        ) {
                            hasVariation = true;
                            break;
                        }
                    }

                    if (hasVariation) {
                        // Screenshot has content, we're done
                        resolve();
                    } else {
                        // All pixels are the same color (blank screenshot), try again
                        return this.attemptScreenshot(renderingCanvas, snapshotTargetContext, options, tries + 1);
                    }
                });
            } else {
                console.warn('Got a blank image after ' + options.maxTries + ' tries');
                resolve();
            }
        });
    }

    public takeAsBlob(args: ISnapshotArgs): Promise<Blob> {
        return this.takeAsDataUrl(args).then(dataUrl => dataURLtoBlob(dataUrl));
    }

    public download(args: ISnapshotArgs): Promise<void> {
        return this.takeAsBlob(args).then(blob => downloadBlob(blob, args.filename));
    }
}
